// Shared helpers for the Billing UI (pricing page, status widget, capacity banner).
// All copy surfaced to merchants is English-only for now (US market); this module
// is structure-only (no user-facing strings) so it needs no i18n wiring itself.

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Mirrors the API_URL detection in src/App.js so the billing endpoints hit the
// same backend the rest of the app talks to, unless a dedicated URL is provided.
export const BILLING_API_URL =
  process.env.REACT_APP_BILLING_API_URL ||
  (isLocalhost ? 'http://localhost:8080' : 'https://mi-chat-892931479853.us-central1.run.app');

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

// The billing routes (billing/routes.py) are @jwt_required() and read the
// Shopify session claims (company_id, shop, auth_provider="shopify") from the
// JWT. That JWT is the UPSELLerate session token issued by oauth_server
// /auth/shopify/exchange and stored by ShopifyComplete.jsx as
// localStorage['token'] — the SAME token App.js already sends to
// /auth/shopify/run-initial-sync. So the caller should pass that token.
//
// If no token prop is passed we fall back to localStorage['token']. If there
// is still no token, or the backend rejects it (401), we surface a legible
// message instead of a bare "Request failed (401)". We NEVER fabricate or send
// an unsigned token — the backend must verify the JWT signature.
function resolveBillingToken(token) {
  if (token) return token;
  try {
    return localStorage.getItem('token') || null;
  } catch {
    return null;
  }
}

async function billingFetch(path, { token, method = 'GET', body } = {}) {
  const resolvedToken = resolveBillingToken(token);
  const headers = { 'Content-Type': 'application/json' };
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${BILLING_API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await parseJsonSafe(response);

  if (!response.ok) {
    if (response.status === 401) {
      // Bad/missing/expired JWT, or a token that is not a Shopify session.
      const err = new Error(
        'Your Shopify session is missing or expired. Open billing from inside the Shopify app so we can verify your session, then try again.'
      );
      err.status = 401;
      throw err;
    }
    const message = data?.error || data?.detail || data?.message || `Request failed (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  return data;
}

// POST /billing/subscribe {plan, interval, addons} -> {confirmationUrl}
export function subscribeToPlan({ token, plan, interval, addons = 0 }) {
  return billingFetch('/billing/subscribe', {
    token,
    method: 'POST',
    body: { plan, interval, addons },
  });
}

// GET /billing/status -> shape defined by billing/account_state.py
// account_to_status_dict():
//   { plan_tier, billing_interval, subscription_id, subscription_status,
//     trial_ends_at, addons_purchased, quota_total, active_opportunities,
//     remaining_slots, can_activate, max_addons, features }
// subscription_status is UPPERCASE: ACTIVE|CANCELLED|FROZEN|DECLINED|PENDING|EXPIRED.
// Backend errors come back as {"error": "..."} (see billing/routes.py).
export function fetchBillingStatus({ token }) {
  return billingFetch('/billing/status', { token });
}

// POST /billing/cancel
export function cancelSubscription({ token }) {
  return billingFetch('/billing/cancel', { token, method: 'POST' });
}
